// Brand Asset — ช่องทางของแบรนด์ และคู่แข่ง
//
// อ่าน/เขียนทั้งก้อน (ฟอร์มเดียว กดบันทึกทีเดียว) จึงไม่มี endpoint ราย row
import type { BrandAssets } from "@contracts/api";
import { api } from "@shared/api/client";

export const assetKeys = {
  byBrand: (brandId: string) => ["brandAssets", brandId] as const,
};

export async function fetchBrandAssets(brandId: string): Promise<BrandAssets> {
  const { data } = await api.get<BrandAssets>(`/brands/${encodeURIComponent(brandId)}/assets`);
  return {
    channels: Array.isArray(data?.channels) ? data.channels : [],
    competitors: Array.isArray(data?.competitors) ? data.competitors : [],
    updatedAt: data?.updatedAt ?? null,
  };
}

export async function saveBrandAssets(
  brandId: string,
  body: Pick<BrandAssets, "channels" | "competitors">,
): Promise<BrandAssets> {
  const { data } = await api.put<BrandAssets>(
    `/brands/${encodeURIComponent(brandId)}/assets`,
    body,
  );
  return data;
}

/** ผลจากปุ่ม AI Suggest — ยังไม่ถูกบันทึก ผู้ใช้ตัดออกได้ก่อนกดบันทึก
 *  รอบแรก (suggestCompetitors) channels จะว่างเสมอ — ช่องทางมาตอน lookupBrandChannels */
export type SuggestedCompetitor = {
  name: string;
  url: string;
  channels: { platform: string; url: string }[];
  note: string;
};

export async function suggestCompetitors(
  brandId: string,
): Promise<{ summary: string; competitors: SuggestedCompetitor[] }> {
  const { data } = await api.post<{ summary?: string; competitors?: SuggestedCompetitor[] }>(
    `/brands/${encodeURIComponent(brandId)}/assets/suggest`,
    {},
    // Claude ค้นเว็บหลายรอบ — ใช้เวลาได้เป็นนาที timeout ปกติ 60 วิของ client สั้นไป
    { timeout: 300_000 },
  );
  return {
    summary: data?.summary ?? "",
    competitors: Array.isArray(data?.competitors) ? data.competitors : [],
  };
}

/** ค้นช่องทางของแบรนด์ที่เลือกไว้ — หลายชื่อในคำขอเดียว คืนรูปเดียวกับ suggestCompetitors */
export async function lookupBrandChannels(
  brandId: string,
  names: string[],
): Promise<{ summary: string; competitors: SuggestedCompetitor[] }> {
  const { data } = await api.post<{ summary?: string; competitors?: SuggestedCompetitor[] }>(
    `/brands/${encodeURIComponent(brandId)}/assets/lookup`,
    { names },
    { timeout: 300_000 },
  );
  return {
    summary: data?.summary ?? "",
    competitors: Array.isArray(data?.competitors) ? data.competitors : [],
  };
}

/** ช่องทางที่ดึงยอดผู้ติดตามได้ — ต้องตรงกับ FOLLOWER_PLATFORMS ฝั่ง server */
export const FOLLOWER_PLATFORMS = ["Facebook", "Instagram", "TikTok", "YouTube"];

/** ยอดผู้ติดตามจาก Apify — ดึงตอนกดปุ่ม ไม่ได้บันทึกลงฐานข้อมูล */
export type ChannelFollowers = {
  platform: string;
  url: string;
  followers: number | null;
  name: string;
  error: string;
};

export async function fetchChannelFollowers(
  brandId: string,
  channels: { platform: string; url: string }[],
): Promise<ChannelFollowers[]> {
  const { data } = await api.post<{ stats?: ChannelFollowers[] }>(
    `/brands/${encodeURIComponent(brandId)}/assets/followers`,
    { channels },
    // Apify รันจริงต่อรายการ — ช้ากว่าการเรียก API ปกติมาก
    { timeout: 300_000 },
  );
  return Array.isArray(data?.stats) ? data.stats : [];
}
